import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Label, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleXmark } from "@fortawesome/free-solid-svg-icons";
import Select from "../../../../components/Inputs/Select";
import Button from "../../../../components/Button";
import Modal from "../../../../components/Modal";
import { useDeleteTimeSheet } from "../../api/useDeleteTimeSheet";
import { useUser } from "../../../../utils/hooks/user";
import { useVisible } from "../../../../utils/hooks/useVisible";
import { useActionWithAccess } from "../../../../utils/hooks/access";

export const useRemoveActions = ({ refetchOverview, setData, setTotalItems }) => {
    const { t } = useTranslation();
    const user = useUser();
    const [rules, setRules] = useState([]);
    const { open, visible, selected, close } = useVisible();

    const onRemoveSuccess = useCallback((ids) => {
        refetchOverview();
        setTotalItems((prev) => prev - ids.length);
        setData((prev) => prev?.filter(
            (prevTimeSheet) => !ids.includes(prevTimeSheet?.id)
        ));
        close();
    }, [refetchOverview, setTotalItems, setData, close]);

    const {
        remove: baseRemove,
        removeMany: baseRemoveMany,
    } = useDeleteTimeSheet();

    const removeOne = useActionWithAccess(
        useCallback((id) => {
            baseRemove(id, onRemoveSuccess.bind(null, [id]));
        }, [baseRemove, onRemoveSuccess]),
        "timeSheet.canDeleteMyTimesheet",
    );

    const removeMany = useActionWithAccess(
        useCallback((ids, data, userId) => {
            baseRemoveMany(ids, data, userId, onRemoveSuccess.bind(null, ids));
        }, [baseRemoveMany, onRemoveSuccess]),
        "timeSheet.canDeleteMyTimesheet",
    );

    const { removeWithoutOverride, removeWithOverride } = useMemo(() => {
        const data = selected?.data || {};
        const dates = selected?.dates || [];
        const userId = user?.id;
        return {
            removeWithoutOverride: Array.isArray(data) ? () => removeMany(data, { rules: [], dates, override: false }, userId) : () => removeOne(data),
            removeWithOverride: () => removeMany(data, { rules, dates, override: true }, userId)
        }
    }, [removeMany, removeOne, rules, selected?.data, selected?.dates, user?.id]);

    const modal = useMemo(() => {
        const timesheets = selected?.timesheets?.filter((timesheet) => selected?.data.includes(timesheet.id));
        const appliedRules = [...new Set(
            timesheets?.map((timesheet) => {
                const values = timesheet?.appliedRules?.map((rule) => rule.replace("--override", ""));
                return values || []
            }).flat())
        ].filter((rule) => rule !== "workedHolidayMarked");

        const count = Array.isArray(selected?.data || {}) ? selected?.data.length : 1;

        return (
            <Modal isOpen={visible} centered>
                <ModalHeader className="d-flex align-items-center justify-content-center py-4 w-100">
                    <FontAwesomeIcon
                        icon={faCircleXmark}
                        className="text-danger w-100 mb-3"
                        fontSize={70}
                    />
                    <h2 className="mb-0 text-center font-weight-bolder">
                        {t("are-you-sure")}
                    </h2>
                </ModalHeader>
                <ModalBody className="d-flex flex-column justify-content-center py-0 mb-4">
                    <p className="text-center">
                        {t("do-you-want-to-delete-timesheets", { count })}
                    </p>

                    {appliedRules?.length > 0 && (
                        <div>
                            <Label>
                                {t("select-options-when-override")}:
                            </Label>
                            <Select
                                placeholder={t("override-rule")}
                                mode="multiple"
                                onChange={(value) => setRules(value)}
                            >
                                {appliedRules.map((rule) => (
                                    <Select.Option key={rule} value={rule}>
                                        {t(rule)}
                                    </Select.Option>
                                ))}
                            </Select>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter className="d-flex justify-content-center align-items-center w-full mx-0 p-0">
                    <Button onClick={close}>
                        {t("cancel")}
                    </Button>
                    {appliedRules?.length > 0 && (
                        <Button
                            className="border border-danger shadow-none text-danger bg-white"
                            disabled={appliedRules?.length > 0 ? rules?.length === 0 : false}
                            onClick={removeWithOverride}
                        >
                            {t("continue-with-override")}
                        </Button>
                    )}
                    <Button
                        color="danger"
                        onClick={removeWithoutOverride}
                    >
                        {appliedRules?.length > 0 ? t("continue-without-override") : t("confirm")}
                    </Button>
                </ModalFooter>
            </Modal>
        )
    }, [close, removeWithOverride, removeWithoutOverride, rules?.length, selected?.data, selected?.timesheets, t, visible]);

    const remove = useActionWithAccess(open, "timeSheet.canDeleteMyTimesheet");

    return { modal, remove };
}
